/**
 * Os blocos de uma tela de lista: indicadores, gráfico e funil, acima da lista.
 *
 * O que estes blocos somam são as LINHAS QUE A LISTA ESTÁ MOSTRANDO — com a busca aplicada, se
 * houver. Não são os totais do período: esses são da Geral, e maiores, porque ela conta envio que
 * não saiu de disparo. Por isso o rótulo diz "desta lista" e as taxas levam a marca de
 * recalculadas: elas vêm dos contadores somados, nunca de média de taxas.
 *
 * Os modelos de gráfico são três para todas as telas — ranking, composição e dispersão — mais um
 * que cada tela traz de si (a situação do disparo, a categoria do template, o provedor da rota),
 * porque só a tela sabe agrupar as próprias linhas.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Settings2 } from 'lucide-react';
import { ChartFrame, type FrameSpec } from '@/components/v2/chart/ChartFrame';
import { colX, curve, niceMax, scaleY, ticks, type Val } from '@/components/v2/chart/geometry';
import { GhostBtn } from '@/components/v2/ui';
import { c, v } from '@/components/v2/theme';
import { HeatGrid, MixBar, Pills, RankBars, SLICE_HUE, ScatterPlot, TOP_N, sliceUp, type Bar, type Dot, type HeatCol } from './chartbits';
import type { BlocksReg, ChartRow, MetricBlocks, MetricsReg, OwnModel } from '../lib/cards';
import { Block } from './Block';
import { ModelPicker } from './ModelPicker';
import { Funnel } from './overview/Funnel';
import { Kpis } from './overview/Kpis';
import { DASH, type Fmtr } from '../lib/format';
import { CHART_METRICS, M, aggregate, readMetric } from '../lib/metrics';
import { SCREEN_BLOCK_META, type ScreenBlock } from '../lib/prefs';
import type { ScreenKey } from '../lib/screens';
import { useEmail } from '../state/store';

export interface ChartModel { k: string; l: string; d: string }

export const SCREEN_CHART_MODELS: ChartModel[] = [
    { k: 'rank', l: 'Ranking', d: 'As maiores linhas na métrica escolhida, em barras.' },
    { k: 'mix', l: 'Composição', d: 'A fatia de cada linha no total somado da lista.' },
    { k: 'scatter', l: 'Métrica × métrica', d: 'Duas métricas à sua escolha; cada linha é um ponto, do tamanho do envio.' },
    { k: 'heat', l: 'Mapa de calor', d: 'Métricas × linhas, verde acima e vermelho abaixo da média da lista.' },
];

/** A linha de receita por dia só entra onde existe série: a tela precisa ter mandado o eixo e os dias. */
const DAILY_MODEL: ChartModel = {
    k: 'daily', l: 'Receita por dia',
    d: 'A receita de cada linha dia a dia, as maiores em curvas separadas.',
};

/**
 * Os modelos desta tela: os três de todas, mais o que ela trouxer.
 *
 * Com um grupo só, o modelo próprio não entra: uma barra sozinha, ou uma composição de 100% numa
 * fatia, não compara nada. Acontece de verdade nas visões que já filtram por aquele agrupamento —
 * "Com envio atribuível" não tem o que comparar com "sem".
 */
export const chartModels = (own?: OwnModel, axis?: MetricBlocks['axis'], rows?: ChartRow[]): ChartModel[] => [
    ...SCREEN_CHART_MODELS,
    ...(axis && rows?.some((r) => r.daily?.length) ? [DAILY_MODEL] : []),
    ...(own && own.items.length > 1 ? [{ k: own.k, l: own.l, d: own.d }] : []),
];

/**
 * O modelo que a tela realmente desenha: a preferência, quando ela existe neste catálogo, e o
 * ranking quando não — trocar de visão troca o catálogo, e a escolha de uma pode não valer na outra.
 * O painel do Personalizar usa a MESMA conta, senão ele marcaria um modelo e a tela desenharia outro.
 */
export const chartModel = (models: ChartModel[], pref: string): string =>
    models.some((x) => x.k === pref) ? pref : 'rank';

/** Os modelos da tela que está no ar, seja ela de métricas ou de números próprios. */
export const regModels = (reg: BlocksReg | null): ChartModel[] =>
    reg == null ? SCREEN_CHART_MODELS
        : reg.kind === 'data' ? reg.models
            : chartModels(reg.own, reg.axis, reg.rows);

/**
 * Nenhuma linha desta lista tem contador de envio. Não é zero: é ausência, e o funil de uma
 * ausência mostraria "enviados 0", que é mentira — os envios existem, só não são desta linha
 * (domínio dividido entre rotas, oferta sem envio atribuível).
 */
const lacksSends = (rows: ChartRow[]) => rows.every((r) => !r.row.sends);

/** Com a lista sem contador de envio, sobram os indicadores de dinheiro — os de volume seriam 0. */
const kpiKeys = (keys: string[], rows: ChartRow[]) =>
    lacksSends(rows) ? keys.filter((k) => M[k]?.block === 'revenue') : keys;

/** Métricas que o ranking oferece: as que respondem "quem é o maior". */
const RANK_METRICS = ['net', 'ecpm', 'sent', 'delivered', 'uniqueOpens', 'uniqueClicks', 'openRate', 'clickRate'];
/** Composição só aceita número que SOMA: repartir uma taxa entre linhas não quer dizer nada. */
const MIX_METRICS = ['net', 'rev', 'sent', 'delivered', 'uniqueOpens', 'uniqueClicks', 'gamImp'];

/** As métricas viram opção de botão: é o catálogo que dá rótulo e descrição. */
const metricOpts = (keys: string[]) => keys.filter((k) => M[k]).map((k) => ({ k, l: M[k].l, d: M[k].d }));

export function ScreenBlocks({ screenKey, rows, own, note, axis, children }: {
    screenKey: ScreenKey;
    rows: ChartRow[];
    own?: OwnModel;
    /** A ressalva desta visão — o que estes números não dizem. */
    note?: ReactNode;
    axis?: MetricBlocks['axis'];
    /** A lista — desenhada na posição que o bloco `list` ocupa na ordem. */
    children: ReactNode;
}) {
    const { prefs, setDrawer, fmt, registerBlocks } = useEmail();
    const p = prefs.screens[screenKey];

    // sem array de dependências de propósito: só escreve uma ref, não dispara renderização.
    // É daqui que o Personalizar tira os blocos para desenhar com o dado real do período.
    useEffect(() => {
        registerBlocks({ kind: 'metrics', rows, own, note, axis });
        return () => registerBlocks(null);
    });
    const hidden = new Set(p.hidden);
    const noSends = lacksSends(rows);
    const totals = useMemo(
        () => aggregate(rows.map((r) => ({ revenue: r.row.revenue ?? null, sends: r.row.sends ?? null }))),
        [rows],
    );
    const models = chartModels(own, axis, rows);
    const model = chartModel(models, p.chart);

    const draw = (b: ScreenBlock) => {
        if (b === 'list') return <div key="list">{children}</div>;
        if (hidden.has(b) || !rows.length) return null;
        if (b === 'kpis') {
            return (
                <Kpis
                    key="kpis"
                    totals={totals}
                    keys={kpiKeys(p.kpis, rows)}
                    style={p.kpiStyle}
                    derived
                    fmt={fmt}
                    sub={`somados das ${rows.length} ${rows.length === 1 ? 'linha' : 'linhas'} desta lista`}
                    markTitle="Recalculada a partir dos contadores somados desta lista"
                    onCustomize={() => setDrawer('kpis')}
                />
            );
        }
        // funil de uma lista sem contador de envio seria quatro zeros, e ausência não é zero
        if (b === 'funnel') return noSends ? null : <Funnel key="funnel" totals={totals} fmt={fmt} />;
        return <ChartBlock key="chart" screenKey={screenKey} rows={rows} own={own} axis={axis} model={model} models={models} fmt={fmt} />;
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {note && !!rows.length && <BlocksNote>{note}</BlocksNote>}
            {p.blocks.map(draw)}
        </div>
    );
}

/** A ressalva da visão, acima dos blocos: o que estes números somam e o que eles não dizem. */
const BlocksNote = ({ children }: { children: ReactNode }) => (
    <p style={{
        margin: 0, padding: '10px 14px', borderRadius: 12, fontSize: 11.5, lineHeight: 1.55,
        border: `1px solid ${v('border-soft')}`, background: v('inset'), color: v('muted'),
    }}>
        {children}
    </p>
);

/**
 * O mesmo desenho, para o painel da direita do Personalizar: o bloco que a aba está editando, com
 * o dado real do período. Bloco desligado aparece mesmo assim, com o aviso — senão a aba em que se
 * está mexendo ficaria em branco, sem dizer por quê.
 */
export function BlocksPreview({ screenKey, reg, only }: { screenKey: ScreenKey; reg: MetricsReg; only: ScreenBlock | 'all' }) {
    const { prefs, fmt } = useEmail();
    const p = prefs.screens[screenKey];
    const totals = useMemo(
        () => aggregate(reg.rows.map((r) => ({ revenue: r.row.revenue ?? null, sends: r.row.sends ?? null }))),
        [reg.rows],
    );
    const models = chartModels(reg.own, reg.axis, reg.rows);
    const model = chartModel(models, p.chart);
    const off = only !== 'all' && p.hidden.includes(only);
    const noSends = lacksSends(reg.rows);
    // na aba de blocos o painel mostra a tela inteira, na ordem escolhida: é a ordem que se edita
    const drawn = only === 'all'
        ? p.blocks.filter((b): b is ScreenBlock => b !== 'list' && !p.hidden.includes(b))
        : [only];

    if (!reg.rows.length) {
        return (
            <p style={{ margin: 0, fontSize: 12.5, color: v('faint'), lineHeight: 1.5 }}>
                A lista desta tela não tem linha no período — os blocos não têm o que somar. Os ajustes ficam
                salvos do mesmo jeito.
            </p>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {off && (
                <p style={{
                    margin: 0, padding: '9px 12px', borderRadius: 10, fontSize: 11.5, lineHeight: 1.5,
                    border: `1px solid ${c('amber', 0.4)}`, color: c('amber'), background: c('amber', 0.07),
                }}>
                    {SCREEN_BLOCK_META[only].l} está desligado nesta tela — aqui ele aparece só para você ver o que
                    está ajustando.
                </p>
            )}
            {reg.note && <BlocksNote>{reg.note}</BlocksNote>}
            {drawn.map((b) => (
                <div key={b}>
                    {b === 'kpis' && (
                        <Kpis totals={totals} keys={kpiKeys(p.kpis, reg.rows)} style={p.kpiStyle} derived fmt={fmt}
                            sub={`somados das ${reg.rows.length} ${reg.rows.length === 1 ? 'linha' : 'linhas'} desta lista`}
                            markTitle="Recalculada a partir dos contadores somados desta lista"
                            onCustomize={() => undefined} />
                    )}
                    {b === 'funnel' && !noSends && <Funnel totals={totals} fmt={fmt} />}
                    {b === 'chart' && (
                        <ChartBlock screenKey={screenKey} rows={reg.rows} own={reg.own} axis={reg.axis} model={model} models={models} fmt={fmt} />
                    )}
                </div>
            ))}
            {noSends && (drawn.includes('funnel') || drawn.includes('kpis')) && (
                <p style={{ margin: 0, fontSize: 11.5, color: v('faint'), lineHeight: 1.5 }}>
                    Nenhuma linha desta visão tem contador de envio próprio: por isso o funil não aparece, e dos
                    indicadores entram só os de dinheiro — zeros diriam que não se enviou nada. Nas outras visões
                    desta tela os dois desenham completos, e a escolha que você fizer aqui vale nelas.
                </p>
            )}
            {only === 'all' && (
                <p style={{ margin: 0, fontSize: 11.5, color: v('faint'), lineHeight: 1.5 }}>
                    A lista vem depois destes blocos — ela não sai da tela, e por isso não tem chave para desligar.
                </p>
            )}
        </div>
    );
}

/* ----------------------------------------------------------------- o bloco do gráfico */

function ChartBlock({ screenKey, rows, own, axis, model, models, fmt }: {
    screenKey: ScreenKey; rows: ChartRow[]; own?: OwnModel; axis?: MetricBlocks['axis'];
    model: string; models: ChartModel[]; fmt: Fmtr;
}) {
    const { setScreenPrefs, setDrawer } = useEmail();
    const spec = models.find((x) => x.k === model) ?? models[0];
    const items = own && model === own.k ? own.items : rows;

    return (
        <Block
            title={spec.l}
            sub={spec.d}
            right={(
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <ModelPicker opts={models} value={model} onChange={(chart) => setScreenPrefs(screenKey, { chart })} />
                    <GhostBtn onClick={() => setDrawer('chart')}>
                        <Settings2 size={14} color={v('muted')} />Gráfico
                    </GhostBtn>
                </div>
            )}
            note={own && model === own.k ? own.note : undefined}
        >
            {model === 'daily' && axis ? <DailyLines rows={rows} axis={axis} fmt={fmt} />
                : model === 'scatter' ? <Scatter rows={rows} fmt={fmt} />
                    : model === 'heat' ? <Heat rows={rows} fmt={fmt} />
                        : model === 'mix' ? <Mix items={items} fmt={fmt} />
                            : <Rank items={items} fmt={fmt} />}
        </Block>
    );
}

/* ----------------------------------------------------------------- ranking */

/** O ranking das linhas na métrica escolhida. O desenho é o de `chartbits`. */
function Rank({ items, fmt }: { items: ChartRow[]; fmt: Fmtr }) {
    const [key, setKey] = useState('net');
    const met = M[key];
    const bars = useMemo<Bar[]>(() => items
        .map((it) => ({ it, n: readMetric(it.row, key) }))
        // ausente vai para o fim nos dois sentidos: "—" não é zero, é outra coisa
        .sort((a, b) => (a.n == null ? 1 : b.n == null ? -1 : b.n - a.n))
        .slice(0, TOP_N)
        .map(({ it, n }) => ({ key: it.key, label: it.label, n, text: fmt.metric(met, n), hue: it.hue ?? met.hue ?? 'blue' })),
        [items, key, met, fmt]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Pills opts={metricOpts(RANK_METRICS)} value={key} onChange={setKey} />
            <RankBars bars={bars} count={items.length} what="linhas" />
        </div>
    );
}

/* ----------------------------------------------------------------- composição */

/** A fatia de cada linha no total somado. Só com número que soma. */
function Mix({ items, fmt }: { items: ChartRow[]; fmt: Fmtr }) {
    const [key, setKey] = useState('net');
    const met = M[key];
    const { slices, total } = useMemo(
        () => sliceUp(items.map((it) => ({
            key: it.key, label: it.label, hue: it.hue,
            n: Math.max(0, readMetric(it.row, key) ?? 0),
        }))),
        [items, key],
    );
    const pills = <Pills opts={metricOpts(MIX_METRICS)} value={key} onChange={setKey} />;

    if (total <= 0) {
        return (
            <>
                {pills}
                <p style={{ margin: 0, fontSize: 12.5, color: v('faint'), lineHeight: 1.5 }}>
                    Nenhuma linha tem {met.l.toLowerCase()} nesta janela — {DASH}, não zero: não há o que repartir.
                </p>
            </>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pills}
            <MixBar slices={slices} total={total} pct={fmt.pct} text={(n) => fmt.metric(met, n)} />
        </div>
    );
}

/* ----------------------------------------------------------------- métrica × métrica */

/** O rótulo do eixo: cada métrica tem o seu formato, e um eixo de receita em "12,5%" seria mentira. */
const axisLabel = (key: string, fmt: Fmtr) => (n: number) => (
    M[key]?.fmt === 'pct1' ? `${n.toFixed(n < 10 ? 1 : 0).replace('.', ',')}%`
        : M[key]?.fmt === 'brl' ? fmt.money(n)
            : n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1).replace('.', ',')} mil` : String(Math.round(n))
);

/** Escolha de um eixo: o mesmo botão do ranking, com o nome do eixo na frente. */
function AxisRow({ l, value, onChange }: { l: string; value: string; onChange: (k: string) => void }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: v('faint'), width: 48, flexShrink: 0 }}>
                {l}
            </span>
            <Pills opts={metricOpts(CHART_METRICS)} value={value} onChange={onChange} />
        </div>
    );
}

/**
 * Duas métricas à escolha, uma bolha por linha, do tamanho do envio. O desenho é o de `chartbits`.
 *
 * Linha sem um dos dois números não entra: um ponto em (0,0) diria "abriu 0%", e "—" não é zero. O
 * rodapé conta quantas ficaram de fora, para a ausência não passar por lista menor.
 */
function Scatter({ rows, fmt }: { rows: ChartRow[]; fmt: Fmtr }) {
    const [xk, setXk] = useState('openRate');
    const [yk, setYk] = useState('clickRate');
    const mx0 = M[xk] ?? M.openRate;
    const my0 = M[yk] ?? M.clickRate;
    const dots = useMemo<Dot[]>(() => rows.flatMap((it) => {
        const x = readMetric(it.row, mx0.k);
        const y = readMetric(it.row, my0.k);
        // linha sem um dos dois não vira bolha: (0,0) diria "deu zero", e ausência não é zero
        if (x == null || y == null) return [];
        return [{ key: it.key, label: it.label, hue: it.hue, x, y, size: readMetric(it.row, 'sent') ?? 0 }];
    }), [rows, mx0, my0]);

    const axes = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <AxisRow l="eixo x" value={mx0.k} onChange={setXk} />
            <AxisRow l="eixo y" value={my0.k} onChange={setYk} />
        </div>
    );

    if (!dots.length) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {axes}
                <p style={{ margin: 0, fontSize: 12.5, color: v('faint'), lineHeight: 1.5 }}>
                    Nenhuma linha tem {mx0.l.toLowerCase()} e {my0.l.toLowerCase()} nesta janela — os dois números são
                    {' '}{DASH} e não há ponto para marcar.
                </p>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {axes}
            <ScatterPlot
                dots={dots}
                xTick={axisLabel(mx0.k, fmt)}
                yTick={axisLabel(my0.k, fmt)}
                tip={(d) => [
                    { l: mx0.l, val: fmt.metric(mx0, d.x), col: c(mx0.hue ?? 'violet') },
                    { l: my0.l, val: fmt.metric(my0, d.y), col: c(my0.hue ?? 'pink') },
                    { l: 'Enviados', val: fmt.int(d.size), col: 'transparent' },
                ]}
                footer={(
                    <>
                        <span>eixo x: {mx0.l.toLowerCase()} · eixo y: {my0.l.toLowerCase()} · tamanho: enviados</span>
                        {dots.length < rows.length && (
                            <span>{rows.length - dots.length} de {rows.length} ficaram de fora por não ter um dos dois números nesta janela</span>
                        )}
                    </>
                )}
            />
        </div>
    );
}

/* ----------------------------------------------------------------- mapa de calor */

/** As colunas do mapa: dinheiro, volume e as taxas que se olha todo dia, nesta ordem. */
const HEAT_METRICS = ['net', 'ecpm', 'sent', 'deliveryRate', 'openRate', 'clickRate', 'bounceRate', 'unsubscribeRate'];
const HEAT_N = 12;

/** Métricas × linhas, contra a média da lista. A grade é a de `chartbits`; aqui só o catálogo. */
function Heat({ rows, fmt }: { rows: ChartRow[]; fmt: Fmtr }) {
    const shown = useMemo(
        () => [...rows]
            .sort((a, b) => (readMetric(b.row, 'sent') ?? 0) - (readMetric(a.row, 'sent') ?? 0))
            .slice(0, HEAT_N),
        [rows],
    );
    const byKey = useMemo(() => new Map(shown.map((r) => [r.key, r])), [shown]);
    const cols: HeatCol[] = HEAT_METRICS.filter((k) => M[k]).map((k) => ({ k, l: M[k].l, d: M[k].d, down: M[k].down }));
    if (!shown.length) return null;

    return (
        <HeatGrid
            rows={shown.map((r) => ({ key: r.key, label: r.label }))}
            cols={cols}
            get={(r, col) => { const it = byKey.get(r.key); return it ? readMetric(it.row, col.k) : null; }}
            text={(col, n) => fmt.metric(M[col.k], n)}
            note={(
                <>
                    A cor é a distância da média desta lista, não de uma meta: verde é acima das outras linhas que
                    você está vendo, não “bom”. Em bounce e descadastro a cor inverte — ali acima da média é pior.
                    Célula cinza com {DASH} é número que não existe nessa linha, e não zero.
                    {rows.length > HEAT_N && <> As {HEAT_N} que mais enviaram, de {rows.length} — a lista inteira fica abaixo.</>}
                </>
            )}
        />
    );
}

/* ----------------------------------------------------------------- receita por dia */

/** Quantas linhas ganham curva própria. Acima disso o gráfico vira um novelo e não se lê nenhuma. */
const LINES_N = 6;

/** Todo dia do período, do primeiro ao último — a série de cada linha é esparsa e precisa de eixo. */
const daysOf = (from: string, to: string): string[] => {
    const out: string[] = [];
    for (const d = new Date(`${from}T00:00:00Z`); d <= new Date(`${to}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + 1)) {
        out.push(d.toISOString().slice(0, 10));
    }
    return out;
};

/**
 * A receita de cada linha dia a dia: as maiores em curvas próprias, o resto somado numa curva apagada.
 *
 * É o gráfico que a lista não dá: um disparo sai num dia e rende por vários, e a curva dele depois do
 * envio é a cauda do disparo — quanto tempo o e-mail continua rendendo.
 *
 * Dois cuidados:
 * 1. **a receita de um dia não é o retorno dos envios daquele dia.** É o que o GAM registrou nele; um
 *    disparo de segunda que rendeu na quarta aparece na quarta. Por isso esta curva nunca se divide pelos
 *    envios do mesmo dia — são duas coortes;
 * 2. dia que o GAM ainda não carregou vira **lacuna**, não zero: a curva corta ali, como na Geral.
 */
function DailyLines({ rows, axis, fmt }: { rows: ChartRow[]; axis: NonNullable<MetricBlocks['axis']>; fmt: Fmtr }) {
    const model = useMemo(() => {
        const dates = daysOf(axis.period.from, axis.period.to);
        const missing = new Set(axis.missing.map((x) => (x.includes(':') ? x.slice(x.indexOf(':') + 1) : x)));
        const withDaily = rows.filter((r) => r.daily?.length);
        const total = (r: ChartRow) => (r.daily ?? []).reduce((a, p) => a + p.net, 0);
        const ranked = [...withDaily].sort((a, b) => total(b) - total(a));
        const serieOf = (list: ChartRow[]): Val[] => {
            const byDay = new Map<string, number>();
            for (const r of list) for (const p of r.daily ?? []) byDay.set(p.date, (byDay.get(p.date) ?? 0) + p.net);
            // dia sem reporte é zero de verdade (não houve receita); dia em carga é lacuna
            return dates.map((d) => (missing.has(d) ? null : byDay.get(d) ?? 0));
        };
        const head = ranked.slice(0, LINES_N).map((r, i) => ({
            key: r.key, label: r.label, s: serieOf([r]), c: c(r.hue ?? SLICE_HUE[i % SLICE_HUE.length]),
        }));
        const rest = ranked.slice(LINES_N);
        const lines = [
            ...head,
            ...(rest.length ? [{ key: '_rest', label: `outras ${rest.length}`, s: serieOf(rest), c: v('line') }] : []),
        ];
        return { dates, lines, shown: withDaily.length, gaps: dates.filter((d) => missing.has(d)).length };
    }, [rows, axis]);

    if (!model.lines.length) {
        return (
            <p style={{ margin: 0, fontSize: 12.5, color: v('faint'), lineHeight: 1.5 }}>
                Nenhuma linha desta lista teve receita no período — não há curva para desenhar.
            </p>
        );
    }

    const mx = niceMax(Math.max(1, ...model.lines.flatMap((l) => l.s.filter((x): x is number => x != null))));
    const y = scaleY(0, mx);
    const n = model.dates.length;
    const spec: FrameSpec = {
        dates: model.dates,
        yt: ticks(0, mx, (x) => fmt.money(x).replace(/,\d{2}$/, '')),
        ytr: [],
        areas: [],
        lines: model.lines.map((l) => ({ d: curve(l.s, y), s: l.c, w: l.key === '_rest' ? 1.25 : 2, op: l.key === '_rest' ? 0.5 : 1 })),
        dots: (i) => model.lines.flatMap((l) => (l.s[i] != null ? [{ x: colX(i, n), y: y(l.s[i] as number), c: l.c }] : [])),
        tip: (i) => model.lines.map((l) => ({ l: l.label, v: fmt.money(l.s[i]), c: l.c, fg: 'inherit' })),
        guide: 'soft',
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <ChartFrame spec={spec} label="Receita de cada linha, dia a dia" height={280} />
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
                {model.lines.map((l) => (
                    <span key={l.key} title={l.label} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: v('text-3'), maxWidth: 240, minWidth: 0 }}>
                        <span style={{ width: 14, height: 0, borderTop: `2px solid ${l.c}`, flexShrink: 0 }} />
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.label}</span>
                    </span>
                ))}
            </div>
            <p style={{ margin: 0, fontSize: 11, color: v('faint'), lineHeight: 1.5 }}>
                É a receita que o GAM <strong style={{ fontWeight: 600 }}>registrou no dia</strong>, não o retorno dos
                envios daquele dia: um disparo de segunda que rendeu na quarta aparece na quarta. Por isso esta curva
                não se divide pelos envios do mesmo dia.
                {model.shown > LINES_N && <> As {LINES_N} de maior receita, de {model.shown} com receita no período; o resto vai na curva apagada.</>}
                {model.gaps > 0 && <> {model.gaps === 1 ? 'Um dia está' : `${model.gaps} dias estão`} em carga no GAM e a curva não passa por {model.gaps === 1 ? 'ele' : 'eles'}.</>}
            </p>
        </div>
    );
}
